import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'
import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'

import { CustomDeadlineRequestOptions } from '@models/types'

const watchEntityKeys = ['customFieldSetsUpdatedAt', 'customFields']

type CustomFieldFilters = {
  owner_id?: number,
  owner_type?: string,
}

type UseCustomFieldsOptions = {
  filters?: CustomFieldFilters,
  performHttpRequests?: boolean,
  requestOptions?: CustomDeadlineRequestOptions,
}

function useCustomFields(options: UseCustomFieldsOptions = {}) {
  const { filters } = options
  const { owner_id: filterOwnerId, owner_type: filterOwnerType } = filters || {}

  const customFields = useSelector(reduxState => reduxState.entities.customFields)

  const {
    updatedEntities: {
      customFields: customFieldsUpdatedAt,
      customFieldSets: customFieldSetsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const filteredCustomFields = useMemo(() => {
    const filtered = Object.values(customFields).filter((customField) => {
      const { owner_id, owner_type } = customField

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? owner_type === filterOwnerType : true

      return matchOwnerId && matchOwnerType
    })

    const sorted = sortArrayBy(filtered, 'asc', field => field.sort || field.label)

    return sorted
  }, [customFieldsUpdatedAt, customFieldSetsUpdatedAt, JSON.stringify(filters)])

  return {
    filteredCustomFields,
    hasCustomFieldSets: !!filteredCustomFields.length,
  }
}

export default useCustomFields
